/**
 * 设备信息工具：通过 navigator.userAgent 解析浏览器/系统/设备品牌型号等
 */

export interface DeviceInfo {
  /** 品牌：Apple / Samsung / Huawei / Xiaomi / Google / Unknown 等 */
  brand: string
  /** 型号：iPhone / iPad / Pixel 7 / Windows PC / Macintosh 等 */
  model: string
  /** 设备唯一标识（持久化到 localStorage） */
  deviceId: string
  /** 操作系统：iOS / Android / Windows / macOS / Linux / Unknown */
  os: string
  /** 操作系统版本，如 "17.2" / "10" / "14.3" */
  osVersion: string
  /** 渠道：web */
  channel: string
  /** 浏览器名 */
  browser: string
  /** 浏览器版本 */
  browserVersion: string
}

const DEVICE_ID_KEY = 'app-device-id'

function getOrCreateDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY)
    if (stored) return stored
  } catch {
    /* ignore */
  }
  // 生成简易 UUID
  const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
  try {
    localStorage.setItem(DEVICE_ID_KEY, id)
  } catch {
    /* ignore */
  }
  return id
}

function detectOS(ua: string): { name: string; version: string } {
  // iOS
  let m = ua.match(/OS\s+(\d+[_.\d]*)/i)
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return {
      name: 'iOS',
      version: m ? m[1].replace(/_/g, '.') : ''
    }
  }

  // Android
  m = ua.match(/Android\s+([\d.]+)/i)
  if (m) {
    return { name: 'Android', version: m[1] }
  }

  // Windows
  m = ua.match(/Windows\s+NT\s+([\d.]+)/i)
  if (m) {
    const nt = m[1]
    const versionMap: Record<string, string> = {
      '10.0': '10',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7'
    }
    return { name: 'Windows', version: versionMap[nt] || nt }
  }

  // macOS
  m = ua.match(/Mac\s+OS\s+X\s+(\d+[_.\d]*)/i)
  if (m) {
    return { name: 'macOS', version: m[1].replace(/_/g, '.') }
  }

  // Linux
  if (/Linux/i.test(ua)) {
    return { name: 'Linux', version: '' }
  }

  return { name: 'Unknown', version: '' }
}

function detectDevice(ua: string, os: string): { brand: string; model: string } {
  // iPhone
  if (/iPhone/i.test(ua)) return { brand: 'Apple', model: 'iPhone' }
  // iPad
  if (/iPad/i.test(ua)) return { brand: 'Apple', model: 'iPad' }

  // Android: 尝试从括号中提取品牌+型号（简单模式）
  if (os === 'Android') {
    const mm = ua.match(/;\s*([^;)]+)\s+Build\//i)
    if (mm) {
      const raw = mm[1].trim()
      // 去除开头可能的 Linux; U; zh-cn; 等前缀
      const parts = raw.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        const brand = parts[0]
        const model = parts.slice(1).join(' ')
        return { brand, model }
      }
      return { brand: 'Android', model: raw }
    }
    // 兼容 Pixel 等含 Pixel 关键字
    if (/Pixel/i.test(ua)) {
      const px = ua.match(/Pixel\s*(\d+)?/i)
      return { brand: 'Google', model: px ? `Pixel ${px[1] || ''}`.trim() : 'Pixel' }
    }
    if (/SM-|Galaxy/i.test(ua)) return { brand: 'Samsung', model: 'Galaxy' }
    if (/HarmonyOS|HUAWEI|EMUI/i.test(ua)) return { brand: 'Huawei', model: 'Huawei' }
    if (/MIUI|Xiaomi|Redmi/i.test(ua)) return { brand: 'Xiaomi', model: 'Xiaomi' }
    if (/OPPO/i.test(ua)) return { brand: 'OPPO', model: 'OPPO' }
    if (/vivo/i.test(ua)) return { brand: 'vivo', model: 'vivo' }
    return { brand: 'Android', model: 'Android' }
  }

  // Mac
  if (os === 'macOS') return { brand: 'Apple', model: 'Macintosh' }
  // Windows
  if (os === 'Windows') return { brand: 'PC', model: 'Windows PC' }
  if (os === 'Linux') return { brand: 'PC', model: 'Linux PC' }

  return { brand: 'Unknown', model: 'Unknown' }
}

function detectBrowser(ua: string): { name: string; version: string } {
  let m: RegExpMatchArray | null
  if ((m = ua.match(/Edg(?:e|A|iOS)?\/(\d+[\d.]*)/i))) {
    return { name: 'Edge', version: m[1] }
  }
  if ((m = ua.match(/Chrome\/(\d+[\d.]*)/i)) && !/Edg|OPR|Brave/i.test(ua)) {
    return { name: 'Chrome', version: m[1] }
  }
  if ((m = ua.match(/Firefox\/(\d+[\d.]*)/i))) {
    return { name: 'Firefox', version: m[1] }
  }
  if ((m = ua.match(/Safari\/(\d+[\d.]*)/i)) && !/Chrome|Edg/i.test(ua)) {
    const v = ua.match(/Version\/(\d+[\d.]*)/i)
    return { name: 'Safari', version: v ? v[1] : m[1] }
  }
  return { name: 'Unknown', version: '' }
}

let cached: DeviceInfo | null = null

/**
 * 获取当前设备信息（带缓存）
 */
export function getDeviceInfo(): DeviceInfo {
  if (cached) return cached

  // SSR / 非浏览器环境兜底
  if (typeof navigator === 'undefined') {
    cached = {
      brand: 'Unknown',
      model: 'Unknown',
      deviceId: '',
      os: 'Unknown',
      osVersion: '',
      channel: 'web',
      browser: 'Unknown',
      browserVersion: ''
    }
    return cached
  }

  const ua = navigator.userAgent
  const { name: os, version: osVersion } = detectOS(ua)
  const { brand, model } = detectDevice(ua, os)
  const { name: browser, version: browserVersion } = detectBrowser(ua)

  cached = {
    brand,
    model,
    deviceId: getOrCreateDeviceId(),
    os,
    osVersion,
    channel: 'web',
    browser,
    browserVersion
  }
  return cached
}
